import { useConfirm } from "components/ConfirmDialog";
import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import {
  type AddContactInput,
  useCompanyApi,
  useCompanyMutations,
} from "hooks/useCompaniesApi";
import { type FC, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toastError } from "utils/toast-utils";

/**
 * 企業マスタ詳細画面 (ISSUE-803 Phase 1C)。
 * @return {JSX.Element} 企業詳細
 */
export const CompanyDetailPage: FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useCompanyApi({ id });
  const {
    updateCompany,
    deleteCompany,
    addDomain,
    removeDomain,
    addContact,
    deleteContact,
    loading: mutating,
  } = useCompanyMutations();
  const { confirm, dialog: confirmDialog } = useConfirm();

  // edit name + description
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [descDraft, setDescDraft] = useState("");

  // domain add
  const [newDomain, setNewDomain] = useState("");
  const [newDomainPrimary, setNewDomainPrimary] = useState(false);

  // contact add
  const [contactDraft, setContactDraft] = useState<AddContactInput>({
    email: "",
    displayName: "",
    optedOut: false,
  });

  if (loading && !data) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <Loading size="md" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="alert alert-error">
          <span>エラー: {error?.message ?? "企業が見つかりません"}</span>
        </div>
        <div className="mt-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/companies")}
          >
            ← 一覧に戻る
          </Button>
        </div>
      </div>
    );
  }

  const startEdit = () => {
    setNameDraft(data.name);
    setDescDraft(data.description ?? "");
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!nameDraft.trim()) return;
    try {
      await updateCompany(data.id, {
        name: nameDraft.trim(),
        description: descDraft.trim() || null,
      });
      setEditing(false);
      await refetch();
    } catch (e) {
      toastError(
        `更新に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  const handleDeleteCompany = async () => {
    const ok = await confirm({
      title: "企業を削除",
      message: "この企業を削除します。配信履歴は保持されます。",
      confirmText: "削除",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await deleteCompany(data.id);
      navigate("/companies");
    } catch (e) {
      toastError(
        `削除に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    try {
      await addDomain(data.id, {
        domain: newDomain.trim(),
        isPrimary: newDomainPrimary,
      });
      setNewDomain("");
      setNewDomainPrimary(false);
      await refetch();
    } catch (e) {
      toastError(
        `ドメイン追加に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    const ok = await confirm({
      title: "ドメインを削除",
      message: `ドメイン "${domain}" を削除しますか?`,
      confirmText: "削除",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await removeDomain(data.id, domain);
      await refetch();
    } catch (e) {
      toastError(
        `削除に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  const handleAddContact = async () => {
    if (!contactDraft.email.trim()) return;
    try {
      await addContact(data.id, {
        email: contactDraft.email.trim(),
        displayName: contactDraft.displayName?.trim() || null,
        optedOut: contactDraft.optedOut ?? false,
      });
      setContactDraft({ email: "", displayName: "", optedOut: false });
      await refetch();
    } catch (e) {
      toastError(
        `連絡先追加に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  const handleDeleteContact = async (contactId: string, email: string) => {
    const ok = await confirm({
      title: "連絡先を削除",
      message: `連絡先 "${email}" を削除しますか?`,
      confirmText: "削除",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await deleteContact(data.id, contactId);
      await refetch();
    } catch (e) {
      toastError(
        `削除に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/companies")}
        >
          ← 企業一覧
        </Button>
      </div>

      <div className="flex items-start justify-between mb-4 gap-2">
        {editing ? (
          <div className="flex-1 space-y-2">
            <input
              type="text"
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value)}
              className="input input-bordered input-sm w-full md:w-96"
              placeholder="企業名"
            />
            <textarea
              value={descDraft}
              onChange={e => setDescDraft(e.target.value)}
              className="textarea textarea-bordered textarea-sm w-full"
              placeholder="メモ"
              rows={2}
            />
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={saveEdit}
                disabled={mutating || !nameDraft.trim()}
              >
                保存
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
              >
                キャンセル
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex-1">
            <PageTitle>{data.name}</PageTitle>
            {data.description && (
              <p className="text-sm text-base-content/60 mt-1">
                {data.description}
              </p>
            )}
          </div>
        )}
        <div className="flex gap-2 shrink-0">
          {!editing && (
            <Button type="button" size="sm" variant="ghost" onClick={startEdit}>
              編集
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={handleDeleteCompany}
            disabled={mutating}
          >
            削除
          </Button>
        </div>
      </div>

      {/* ドメイン */}
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold mb-3">ドメイン</h2>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newDomain}
            onChange={e => setNewDomain(e.target.value)}
            placeholder="例: acme.co.jp"
            className="input input-bordered input-sm w-64"
          />
          <label className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={newDomainPrimary}
              onChange={e => setNewDomainPrimary(e.target.checked)}
              className="checkbox checkbox-sm"
            />
            主ドメインに設定
          </label>
          <Button
            type="button"
            size="sm"
            onClick={handleAddDomain}
            disabled={mutating || !newDomain.trim()}
          >
            追加
          </Button>
        </div>
        {data.domains.length === 0 ? (
          <p className="text-sm text-base-content/60">
            まだドメインが登録されていません
          </p>
        ) : (
          <ul className="space-y-1">
            {data.domains.map(d => (
              <li key={d.id} className="flex items-center gap-2 text-sm">
                <code className="bg-slate-100 px-2 py-0.5 rounded">
                  {d.domain}
                </code>
                {d.isPrimary && (
                  <StatusBadge tone="soft-warning">主</StatusBadge>
                )}
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  onClick={() => handleRemoveDomain(d.domain)}
                  disabled={mutating}
                >
                  削除
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 連絡先 */}
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold mb-3">連絡先</h2>
        <div className="flex gap-2 mb-3 flex-wrap">
          <input
            type="email"
            value={contactDraft.email}
            onChange={e =>
              setContactDraft(s => ({ ...s, email: e.target.value }))
            }
            placeholder="email (必須)"
            className="input input-bordered input-sm w-64"
          />
          <input
            type="text"
            value={contactDraft.displayName ?? ""}
            onChange={e =>
              setContactDraft(s => ({ ...s, displayName: e.target.value }))
            }
            placeholder="表示名 (任意)"
            className="input input-bordered input-sm w-48"
          />
          <Button
            type="button"
            size="sm"
            onClick={handleAddContact}
            disabled={mutating || !contactDraft.email.trim()}
          >
            追加
          </Button>
        </div>
        {data.contacts.length === 0 ? (
          <p className="text-sm text-base-content/60">
            まだ連絡先が登録されていません
          </p>
        ) : (
          <Table.Root density="compact">
            <Table.Head>
              <tr>
                <Table.HeaderCell>email</Table.HeaderCell>
                <Table.HeaderCell>表示名</Table.HeaderCell>
                <Table.HeaderCell>状態</Table.HeaderCell>
                <Table.HeaderCell>操作</Table.HeaderCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {data.contacts.map(c => (
                <Table.Row key={c.id}>
                  <Table.Cell>{c.email}</Table.Cell>
                  <Table.Cell>{c.displayName ?? "—"}</Table.Cell>
                  <Table.Cell>
                    {c.optedOut ? (
                      <StatusBadge tone="soft-error">配信停止</StatusBadge>
                    ) : (
                      <StatusBadge tone="soft-success">配信可</StatusBadge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      type="button"
                      size="xs"
                      variant="ghost"
                      onClick={() => handleDeleteContact(c.id, c.email)}
                      disabled={mutating}
                    >
                      削除
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </section>

      {/* 所属グループ */}
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold mb-3">所属企業グループ</h2>
        {data.groupIds.length === 0 ? (
          <p className="text-sm text-base-content/60">
            まだグループに所属していません
          </p>
        ) : (
          <ul className="text-sm">
            {data.groupIds.map(gid => (
              <li key={gid}>
                <a
                  href={`/company-groups/${gid}`}
                  className="link link-primary"
                >
                  {gid}
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
      {confirmDialog}
    </div>
  );
};

export default CompanyDetailPage;
